// Subscription utilities for BuildMyShifts
// Handles pricing logic including BuildMyChecks bundle detection

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const SUBSCRIPTION_STATUS = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELING: 'canceling',
  CANCELLED: 'cancelled',
  CANCELED: 'canceled',
  LOCKED: 'locked',
};

export const PRICING = {
  STANDALONE: 5.00,    // £5/month per user
  BUNDLE: 2.50,        // £2.50/month if also subscribed to BuildMyChecks
  CURRENCY: 'gbp',
  TRIAL_DAYS: 14,
};

/**
 * Check if organisation has an active BuildMyChecks subscription
 * This enables bundle pricing at £2.50/month instead of £5/month
 */
export async function checkBuildMyChecksSubscription(companyId) {
  if (!companyId || typeof companyId !== 'string') {
    return { hasActiveSubscription: false, status: null, productName: 'BuildMyChecks' };
  }
  try {
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    if (!companyDoc.exists()) return false;

    const data = companyDoc.data();
    const status = data.subscriptionStatus;
    const bmcActive = ['active', 'trial'].includes(status);

    return {
      hasActiveSubscription: bmcActive,
      status,
      productName: 'BuildMyChecks',
    };
  } catch (error) {
    console.error('Error checking BuildMyChecks subscription:', error);
    return { hasActiveSubscription: false, status: null, productName: 'BuildMyChecks' };
  }
}

/**
 * Get the correct price for a BuildMyShifts subscription
 */
export async function getEffectivePrice(companyId) {
  const bmcCheck = await checkBuildMyChecksSubscription(companyId);

  return {
    pricePerMonth: bmcCheck.hasActiveSubscription ? PRICING.BUNDLE : PRICING.STANDALONE,
    isBundle: bmcCheck.hasActiveSubscription,
    currency: PRICING.CURRENCY,
    buildMyChecksStatus: bmcCheck,
  };
}

/**
 * Check if a BuildMyShifts subscription is in a valid state
 */
export function isSubscriptionActive(statusOrCompany) {
  if (!statusOrCompany) return false;

  // Accept either a status string or a company object
  const status = typeof statusOrCompany === 'string'
    ? statusOrCompany
    : (statusOrCompany.shiftsSubscriptionStatus || statusOrCompany.subscriptionStatus);

  if (['active'].includes(status)) return true;

  // Trial check
  if (status === 'trialing' || status === 'trial') {
    // If a company object was passed, check trial end date
    if (typeof statusOrCompany === 'object') {
      const trialEnd = statusOrCompany.shiftsTrialEndsAt || statusOrCompany.trialEndsAt;
      if (trialEnd) {
        const endDate = trialEnd.toDate ? trialEnd.toDate() : new Date(trialEnd);
        return endDate > new Date();
      }
    }
    return true; // status is trialing, assume active if no end date to check
  }

  return false;
}

/**
 * Get trial days remaining
 */
export function getTrialDaysRemaining(trialEndsAtOrCompany) {
  // Accept either a raw timestamp/string or a company object
  let trialEnd;
  if (trialEndsAtOrCompany && typeof trialEndsAtOrCompany === 'object' && !trialEndsAtOrCompany.toDate) {
    // It's a company object
    trialEnd = trialEndsAtOrCompany.shiftsTrialEndsAt;
  } else {
    // It's a raw value (string, Timestamp, etc.)
    trialEnd = trialEndsAtOrCompany;
  }
  if (!trialEnd) return 0;
  const endDate = trialEnd.toDate ? trialEnd.toDate() : new Date(trialEnd);
  const now = new Date();
  const diff = endDate - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

const subscriptionUtils = {
  SUBSCRIPTION_STATUS,
  PRICING,
  checkBuildMyChecksSubscription,
  getEffectivePrice,
  isSubscriptionActive,
  getTrialDaysRemaining,
};
export default subscriptionUtils;
